import { useQueryClient } from '@tanstack/react-query';
import { CategoryScale, Chart as ChartJS, Tooltip as ChartTooltip, Filler, LineElement, LinearScale, PointElement } from 'chart.js';
import { useMemo } from 'react';
import { Line } from 'react-chartjs-2';

import Badge from '@/components/Badge/Badge';
import Modal from '@/components/Modal/Modal';
import WidgetDetailChrome from '@/features/dashboard/components/WidgetDetailChrome';
import useSauna from '@/features/homeAutomation/hooks/useSauna';
import useSaunaHistory from '@/features/homeAutomation/hooks/useSaunaHistory';
import { saunaSessionHistoryQueryKey } from '@/features/homeAutomation/hooks/useSaunaSessionHistory';

import SaunaSessionHistorySection from './SaunaSessionHistorySection';
import { formatTempC as fmtTempC } from './sessionHistory';

ChartJS.register(LineElement, PointElement, LinearScale, CategoryScale, ChartTooltip, Filler);

interface SaunaDetailModalProps {
  open: boolean;
  onClose: () => void;
  homeId: string;
  historyDays: number;
  lastUpdated?: Date | null;
  onRefresh?: () => void;
}

function fmtTime(ms: number): string {
  return new Date(ms).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
}

function fmtIsoTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const ms = Date.parse(iso);
  if (!Number.isFinite(ms)) return '—';
  return new Date(ms).toLocaleString();
}

export default function SaunaDetailModal({ open, onClose, homeId, historyDays, lastUpdated, onRefresh }: SaunaDetailModalProps) {
  const sauna = useSauna(homeId);
  const history = useSaunaHistory(homeId, historyDays, open);
  const queryClient = useQueryClient();

  const handleRefresh = onRefresh
    ? () => {
        onRefresh();
        void queryClient.invalidateQueries({ queryKey: saunaSessionHistoryQueryKey(homeId) });
      }
    : undefined;

  const cs = sauna.data?.currentState;
  const cfg = sauna.data?.config;

  const chartData = useMemo(() => {
    const points = history.data?.points ?? [];
    return {
      labels: points.map((p) => fmtTime(Date.parse(p.observedAt))),
      datasets: [
        {
          label: 'Temperature',
          data: points.map((p) => p.temperatureC),
          borderColor: 'rgb(var(--accent-primary-rgb, 99 102 241))',
          backgroundColor: 'rgba(99, 102, 241, 0.12)',
          borderWidth: 2,
          pointRadius: 0,
          tension: 0.25,
          fill: true,
          spanGaps: true,
        },
      ],
    };
  }, [history.data]);

  const chartOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      animation: false as const,
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            label: (ctx: { parsed: { y: number | null } }) => (ctx.parsed.y == null ? '—' : `${ctx.parsed.y.toFixed(1)} °C`),
          },
        },
      },
      scales: {
        x: {
          ticks: { maxTicksLimit: 6, color: 'rgb(var(--text-tertiary-rgb, 156 163 175))' },
          grid: { display: false },
        },
        y: {
          ticks: { color: 'rgb(var(--text-tertiary-rgb, 156 163 175))' },
          grid: { color: 'rgba(127,127,127,0.12)' },
        },
      },
    }),
    [],
  );

  const hasPoints = (history.data?.points?.length ?? 0) > 1;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Sauna"
      description={`Last ${historyDays} day${historyDays === 1 ? '' : 's'}`}
      size="lg"
      manageHistory
    >
      <WidgetDetailChrome widgetId="sauna" lastUpdated={lastUpdated} onRefresh={handleRefresh} />
      <div className="flex flex-col gap-4">
        <div className="flex flex-wrap items-center gap-3">
          {cs?.phase && <Badge tone="info">{cs.phase}</Badge>}
          <div className="text-2xl font-semibold text-(--text-primary) tabular-nums">{fmtTempC(cs?.currentTemperatureC ?? null)}</div>
          {cs?.targetTemperatureC != null && <div className="text-sm text-(--text-tertiary)">target {fmtTempC(cs.targetTemperatureC)}</div>}
        </div>

        <div className="h-56 w-full rounded-md border border-(--border-subtle) bg-(--bg-surface-strong) p-2">
          {history.isPending && <div className="text-sm text-(--text-tertiary)">Loading history…</div>}
          {history.isError && <div className="text-sm text-(--accent-danger)">Failed to load history.</div>}
          {!history.isPending && !history.isError && !hasPoints && <div className="text-sm text-(--text-tertiary)">No history yet.</div>}
          {hasPoints && <Line data={chartData} options={chartOptions} />}
        </div>

        <dl className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
          <DetailRow label="Estimated ready" value={fmtIsoTime(cs?.estimatedReadyAt ?? null)} />
          <DetailRow label="Estimate source" value={cs?.estimateSource ?? '—'} />
          <DetailRow label="Heating rate" value={cs?.heatingRateCPerMin != null ? `${cs.heatingRateCPerMin.toFixed(2)} °C/min` : '—'} />
          <DetailRow label="Start temp" value={fmtTempC(cs?.startTemperatureC ?? null)} />
          <DetailRow label="Ready since" value={fmtIsoTime(cs?.readySince ?? null)} />
          <DetailRow label="Last transition" value={fmtIsoTime(cs?.lastTransitionAt ?? null)} />
          <DetailRow label="Session id" value={cs?.sessionId ?? '—'} />
          <DetailRow label="Reset threshold" value={cfg ? `${cfg.resetThresholdC.toFixed(1)} °C` : '—'} />
        </dl>

        <div className="border-t border-(--border-subtle) pt-4">
          <SaunaSessionHistorySection homeId={homeId} enabled={open} />
        </div>
      </div>
    </Modal>
  );
}

function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between gap-3 border-b border-(--border-subtle)/60 py-1">
      <dt className="text-xs tracking-wide text-(--text-tertiary) uppercase">{label}</dt>
      <dd className="truncate text-right font-medium text-(--text-primary)">{value}</dd>
    </div>
  );
}
